/* global __app_id, __initial_auth_token */
import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken, 
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot,
  writeBatch
} from 'firebase/firestore';
import { 
  Trophy, 
  Users, 
  History,
  LayoutDashboard,
  AlertCircle,
  Edit2, 
  Trash2, 
  X, 
  Loader2, 
  Zap, 
  CheckCircle2, 
  PlusCircle, 
  Gamepad2, 
  Target,
  CalendarDays,
  Play,
  RefreshCw,
  RotateCcw,
  HelpCircle,
  PartyPopper,
  Crown,
  Medal,
  TrendingUp
} from 'lucide-react';

// --- FIREBASE CONFIGURATION ---
const firebaseConfig = {
  apiKey: "AIzaSyBr33zUQC6kPNK6WI41GSjmRKpZb3_jFyI",
  authDomain: "hrms-13230.firebaseapp.com",
  projectId: "hrms-13230",
  storageBucket: "hrms-13230.firebasestorage.app",
  messagingSenderId: "787521971889",
  appId: "1:787521971889:web:5f50ff5c1084d4b69b6490"
};

const ROLES = ["Jungler", "EXP Laner", "Mid Laner", "Gold Laner", "Roamer"];

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const TOURNAMENT_ID = typeof __app_id !== 'undefined' ? __app_id : 'mlbb-tournament-v1';

const App = () => {
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [globalError, setGlobalError] = useState('');
  
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const [team1Id, setTeam1Id] = useState('');
  const [team2Id, setTeam2Id] = useState('');
  const [winnerId, setWinnerId] = useState('');
  const [bonusT1, setBonusT1] = useState({ maniac: false, savage: false });
  const [bonusT2, setBonusT2] = useState({ maniac: false, savage: false });
  const [currentScheduleId, setCurrentScheduleId] = useState(null);

  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamIcon, setNewTeamIcon] = useState('');
  const [newPlayers, setNewPlayers] = useState(ROLES.map(role => ({ name: '', role, userId: '' })));

  // --- 1. AUTHENTICATION ---
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token).catch(async () => {
            await signInAnonymously(auth);
          });
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Auth error:", err);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- 2. DATA SYNC ---
  useEffect(() => {
    if (!user) return;

    const teamsRef = collection(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'teams');
    const unsubTeams = onSnapshot(teamsRef, (snapshot) => {
      setTeams(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (err) => setGlobalError("Database access denied. Check Firebase rules."));

    const matchesRef = collection(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'matches');
    const unsubMatches = onSnapshot(matchesRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMatches(data.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)));
    });

    const scheduleRef = collection(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'schedules');
    const unsubSchedule = onSnapshot(scheduleRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSchedules(data.sort((a, b) => a.order - b.order));
    });

    return () => { unsubTeams(); unsubMatches(); unsubSchedule(); };
  }, [user]);

  // --- LOGIC ---
  const sortedTeams = [...teams].sort((a, b) => {
    const sA = (Number(a.stats?.points) || 0) + (Number(a.stats?.bonusPoints) || 0);
    const sB = (Number(b.stats?.points) || 0) + (Number(b.stats?.bonusPoints) || 0);
    if (sB !== sA) return sB - sA;
    return (Number(b.stats?.win) || 0) - (Number(a.stats?.win) || 0);
  });

  const isGroupPhaseFinished = schedules.length > 0 && schedules.filter(s => s.type === 'group').every(s => s.status === 'completed');
  
  // Finish Detection
  const grandFinalSch = schedules.find(s => s.type === 'final');
  const isTournamentFinished = grandFinalSch?.status === 'completed';
  const finalMatchRecord = matches.find(m => m.scheduleId === (grandFinalSch?.id || ""));
  const championTeam = finalMatchRecord ? (finalMatchRecord.team1.isWinner ? teams.find(t => t.id === finalMatchRecord.team1.id) : teams.find(t => t.id === finalMatchRecord.team2.id)) : null;

  const toggleBonus = (teamNum, type) => {
    const setter = teamNum === 1 ? setBonusT1 : setBonusT2;
    setter(prev => ({
      maniac: type === 'maniac' ? !prev.maniac : false,
      savage: type === 'savage' ? !prev.savage : false
    }));
  };

  const openTeamForm = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setNewTeamName(team.name);
      setNewTeamIcon(team.icon || '');
      setNewPlayers(team.players);
    } else {
      setEditingTeam(null);
      setNewTeamName('');
      setNewTeamIcon('');
      setNewPlayers(ROLES.map(role => ({ name: '', role, userId: '' })));
    }
    setShowTeamForm(true);
  };

  const saveTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    const data = {
      name: String(newTeamName).toUpperCase(),
      icon: String(newTeamIcon),
      players: newPlayers.map(p => ({ name: String(p.name), role: String(p.role), userId: String(p.userId) })),
      stats: editingTeam ? editingTeam.stats : { played: 0, win: 0, loss: 0, points: 0, bonusPoints: 0 }
    };
    try {
      if (editingTeam) await updateDoc(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'teams', editingTeam.id), data);
      else await addDoc(collection(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'teams'), data);
      setShowTeamForm(false);
      setEditingTeam(null);
    } catch (err) { setGlobalError("Failed to save squad."); }
  };

  const clearTournamentHistory = async () => {
    if (matches.length === 0) return alert("History is already empty.");
    if (!window.confirm("Delete ALL match history and reset points?")) return;
    try {
      setLoading(true);
      const batch = writeBatch(db);
      matches.forEach(m => batch.delete(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'matches', m.id)));
      teams.forEach(t => batch.update(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'teams', t.id), {
        stats: { played: 0, win: 0, loss: 0, points: 0, bonusPoints: 0 }
      }));
      schedules.forEach(s => batch.update(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'schedules', s.id), { status: 'pending' }));
      await batch.commit();
    } catch (err) { setGlobalError("Failed to reset."); } finally { setLoading(false); }
  };

  const generateSchedule = async () => {
    if (teams.length < 2) return alert("At least 2 teams required.");
    if (!window.confirm("Build new tournament scheme? Existing progress will be reset.")) return;

    try {
      setLoading(true);
      const batch = writeBatch(db);
      schedules.forEach(s => batch.delete(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'schedules', s.id)));

      const teamList = [...teams];
      const matchEntries = [];
      let order = 0;

      // Group Stage (Round Robin)
      for (let i = 0; i < teamList.length; i++) {
        for (let j = i + 1; j < teamList.length; j++) {
          matchEntries.push({
            team1Id: teamList[i].id,
            team1Name: teamList[i].name,
            team2Id: teamList[j].id,
            team2Name: teamList[j].name,
            status: 'pending',
            type: 'group',
            label: `Group Match`,
            order: order++
          });
        }
      }

      // Final
      matchEntries.push({
        team1Id: null,
        team1Name: null,
        team2Id: null,
        team2Name: null,
        status: 'pending',
        type: 'final',
        label: 'GRAND FINAL',
        order: order++
      });

      for (const m of matchEntries) {
        const newDoc = doc(collection(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'schedules'));
        batch.set(newDoc, m);
      }
      await batch.commit();
      setActiveTab('schedule');
    } catch (err) { setGlobalError("Failed to build schedule."); } finally { setLoading(false); }
  };

  const addMatch = async (e) => {
    e.preventDefault();
    if (!winnerId) return;
    const t1 = teams.find(t => t.id === team1Id);
    const t2 = teams.find(t => t.id === team2Id);
    const bonusVal = (b) => (b.maniac ? 1 : 0) + (b.savage ? 2 : 0);

    const payload = {
      team1: { id: t1.id, name: String(t1.name), icon: String(t1.icon || ''), isWinner: winnerId === t1.id, bonus: bonusT1, bonusPts: bonusVal(bonusT1) },
      team2: { id: t2.id, name: String(t2.name), icon: String(t2.icon || ''), isWinner: winnerId === t2.id, bonus: bonusT2, bonusPts: bonusVal(bonusT2) },
      timestamp: Date.now(),
      date: new Date().toLocaleString('en-US'),
      scheduleId: currentScheduleId
    };

    try {
      await addDoc(collection(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'matches'), payload);
      const teamUpdate = async (team, won, b) => {
        await updateDoc(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'teams', team.id), {
          "stats.played": (Number(team.stats.played) || 0) + 1,
          "stats.win": won ? (Number(team.stats.win) || 0) + 1 : (Number(team.stats.win) || 0),
          "stats.loss": !won ? (Number(team.stats.loss) || 0) + 1 : (Number(team.stats.loss) || 0),
          "stats.points": won ? (Number(team.stats.points) || 0) + 3 : (Number(team.stats.points) || 0),
          "stats.bonusPoints": (Number(team.stats.bonusPoints) || 0) + b
        });
      };
      await teamUpdate(t1, winnerId === t1.id, payload.team1.bonusPts);
      await teamUpdate(t2, winnerId === t2.id, payload.team2.bonusPts);
      
      if (currentScheduleId) {
        await updateDoc(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'schedules', currentScheduleId), { status: 'completed' });
      }
      
      setShowMatchForm(false);
      setWinnerId('');
      setCurrentScheduleId(null);
    } catch (err) { setGlobalError("Failed to save match."); }
  };

  const TeamIcon = ({ icon, className = "w-6 h-6", isPlaceholder = false }) => {
    if (isPlaceholder) return <div className={`${className} bg-slate-50 rounded-lg flex items-center justify-center text-slate-200 border border-slate-100 shadow-sm`}><HelpCircle className="w-4 h-4" /></div>;
    if (!icon) return <div className={`${className} bg-slate-50 rounded-lg flex items-center justify-center text-[10px] border border-slate-100 text-slate-300 font-bold select-none`}>⚔️</div>;
    if (String(icon).length < 5) return <div className={`${className} flex items-center justify-center text-sm font-bold`}>{String(icon)}</div>;
    return <img src={String(icon)} alt="icon" className={`${className} rounded-lg object-cover border border-slate-100 shadow-sm`} onError={(e) => { e.target.onerror = null; e.target.src="https://via.placeholder.com/150?text=MLBB"; }} />;
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-sans">
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 border-2 border-slate-100 border-t-red-600 rounded-full animate-spin mb-4"></div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest animate-pulse italic">Synchronizing Nexus...</p>
        </div>
      </div>
    );
  }

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'schedule', icon: CalendarDays, label: 'Scheme' },
    { id: 'teams', icon: Users, label: 'Squads' },
    { id: 'matches', icon: History, label: 'History' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD] text-slate-800 font-sans antialiased pb-20 lg:pb-0">
      {/* Sidebar PC */}
      <aside className="fixed left-0 top-0 bottom-0 w-60 bg-white border-r border-slate-100 z-50 hidden lg:flex flex-col items-center py-10 shadow-sm">
        <div className="flex items-center gap-2 px-6 mb-12">
          <div className="bg-red-600 p-2 rounded-xl shadow-lg shadow-red-50">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <span className="font-black text-lg tracking-tighter text-slate-900 uppercase">PRO TRACK</span>
        </div>
        <nav className="flex-1 w-full px-4 space-y-1.5">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3.5 p-3 rounded-xl transition-all duration-200 font-bold text-[10px] uppercase tracking-widest ${
                activeTab === item.id 
                  ? 'bg-red-50 text-red-600 border border-red-100/50' 
                  : 'text-slate-400 hover:bg-slate-50'
              }`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Bottom Nav Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-slate-100 z-[100] flex lg:hidden items-center justify-around py-3 shadow-2xl">
        {navItems.map((item) => (
          <button key={item.id} onClick={() => setActiveTab(item.id)} className={`flex flex-col items-center gap-1 transition-all ${activeTab === item.id ? 'text-red-600 scale-105' : 'text-slate-400'}`}>
            <item.icon className="w-5 h-5" />
            <span className="text-[8px] font-bold uppercase tracking-tighter">{item.label}</span>
          </button>
        ))}
      </nav>

      <main className="px-4 py-6 lg:pl-[17rem] lg:pr-10 lg:py-10 max-w-6xl mx-auto lg:mx-0">
        <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8 text-center lg:text-left">
          <div>
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-1.5">
               <div className="w-6 h-[1.5px] bg-red-600"></div>
               <p className="text-red-600 text-[8px] font-bold uppercase tracking-widest italic">Official Championship Arena</p>
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900 leading-none uppercase italic">Tournament Tracker</h1>
          </div>
          <div className="flex flex-wrap justify-center lg:justify-end items-center gap-2">
             <button onClick={clearTournamentHistory} className="bg-white hover:bg-red-50 text-slate-300 hover:text-red-500 border border-slate-100 p-2.5 rounded-xl transition-all shadow-sm group" title="Reset History">
                <RotateCcw className="w-4 h-4 group-hover:rotate-[-90deg] transition-all" />
             </button>
             <button onClick={() => openTeamForm()} className="bg-white hover:bg-slate-50 text-slate-800 border border-slate-100 px-4 py-2.5 rounded-xl font-bold text-[9px] uppercase tracking-widest transition-all flex items-center gap-2 shadow-sm">
                <PlusCircle className="w-3.5 h-3.5 text-red-500" /> Enrollment
             </button>
             <button onClick={generateSchedule} className="bg-white hover:bg-slate-50 text-slate-800 border border-slate-100 px-4 py-2.5 rounded-xl font-bold text-[9px] uppercase tracking-widest transition-all flex items-center gap-2 shadow-sm">
                <RefreshCw className="w-3.5 h-3.5 text-blue-500" /> Build Scheme
             </button>
          </div>
        </header>

        {activeTab === 'dashboard' && (
          <div className="space-y-6 animate-in fade-in duration-500">
            {/* CHAMPION CELEBRATION */}
            {isTournamentFinished && championTeam && (
               <div className="bg-white border-2 border-red-600 rounded-[2.5rem] p-8 md:p-12 shadow-[0_20px_60px_rgba(220,38,38,0.1)] relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-10 opacity-5 rotate-12 group-hover:rotate-0 transition-transform duration-1000">
                     <Crown className="w-64 h-64 text-red-600" />
                  </div>
                  <div className="relative z-10 flex flex-col items-center text-center">
                     <div className="flex items-center gap-3 mb-4">
                        <PartyPopper className="w-10 h-10 text-red-600 animate-bounce" />
                        <h2 className="text-slate-900 text-3xl md:text-5xl font-black italic uppercase tracking-tighter">TOURNAMENT CHAMPION!</h2>
                        <PartyPopper className="w-10 h-10 text-red-600 animate-bounce" style={{ animationDelay: '0.2s' }} />
                     </div>
                     <p className="text-slate-400 text-xs font-bold uppercase tracking-[0.4em] mb-10 italic">The Matrix Conclusion</p>
                     
                     <div className="bg-[#F8FAFC] border border-slate-100 p-8 md:p-12 rounded-[3.5rem] flex flex-col items-center gap-8 shadow-inner max-w-xl w-full text-center">
                        <TeamIcon icon={championTeam.icon} className="w-24 h-24 md:w-40 md:h-40 rounded-[2.5rem] shadow-xl border-4 border-white" />
                        <div>
                           <div className="flex items-center justify-center gap-3 mb-2">
                              <Medal className="w-5 h-5 text-yellow-500" />
                              <p className="text-red-600 text-xs font-black uppercase tracking-widest italic leading-none">The Ultimate Nexus Winner</p>
                              <Medal className="w-5 h-5 text-yellow-500" />
                           </div>
                           <h3 className="text-slate-900 text-3xl md:text-5xl font-black uppercase italic tracking-tighter leading-tight drop-shadow-sm">{championTeam.name}</h3>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full border-t border-slate-200 pt-8">
                           {championTeam.players.map((p, idx) => (
                              <div key={idx} className="bg-white p-3 rounded-2xl border border-slate-100 text-center">
                                 <p className="text-slate-900 font-black text-xs uppercase italic truncate">{p.name || "---"}</p>
                                 <p className="text-[8px] text-slate-400 font-bold uppercase tracking-tighter mt-1">{p.role}</p>
                              </div>
                           ))}
                        </div>
                     </div>
                  </div>
               </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Total Matches', val: matches.length, icon: Gamepad2 },
                { label: 'Top Rank #1', val: sortedTeams[0]?.name || '---', icon: Trophy, color: 'border-red-500', isTeam: true, iconSrc: sortedTeams[0]?.icon },
                { label: 'High Score', val: `${(sortedTeams[0]?.stats?.points || 0) + (sortedTeams[0]?.stats?.bonusPoints || 0)}`, icon: Zap, isPts: true }
              ].map((stat, i) => (
                <div key={i} className={`bg-white p-5 rounded-2xl border ${stat.color || 'border-slate-100'} relative overflow-hidden group shadow-sm hover:shadow-md transition-all`}>
                  <p className="text-slate-400 text-[8px] font-black uppercase tracking-widest mb-2 italic">{stat.label}</p>
                  <div className="flex items-center gap-2 relative z-10">
                    {stat.isTeam && sortedTeams.length > 0 && <TeamIcon icon={stat.iconSrc} className="w-7 h-7" />}
                    <div className="text-lg font-black text-slate-900 italic tracking-tighter uppercase truncate leading-none">
                      {stat.val} {stat.isPts && <span className="text-[9px] text-slate-300 font-bold ml-0.5">PTS</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* LEADERBOARD TABLE */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-50 bg-slate-50/30 flex items-center justify-between">
                <div className="flex items-center gap-2 font-black text-xs uppercase italic text-slate-800">
                  <Target className="w-4 h-4 text-red-600 animate-pulse" /> Live Standings
                </div>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full border-collapse min-w-[550px]">
                  <thead>
                    <tr className="text-slate-400 text-[9px] font-bold uppercase tracking-[0.2em] bg-slate-50/50 text-center">
                      <th className="px-6 py-4 text-left w-14 italic">Pos</th>
                      <th className="px-6 py-4 text-left">Squad Identity</th>
                      <th className="px-6 py-4">GP</th>
                      <th className="px-6 py-4 text-green-600/70 w-16">W</th>
                      <th className="px-6 py-4 text-red-600/70 w-16">L</th>
                      <th className="px-6 py-4 text-right text-red-600 pr-10">Total Pts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedTeams.map((team, index) => {
                      const isChampion = isTournamentFinished && team.id === championTeam?.id;
                      return (
                        <tr key={team.id} className="hover:bg-slate-50/30 transition-colors group text-center">
                          <td className="px-6 py-3.5 text-left">
                            <div className={`w-7 h-7 flex items-center justify-center rounded-lg font-black text-xs italic ${index === 0 ? 'bg-red-600 text-white shadow-md' : 'bg-slate-50 text-slate-300'}`}>{index + 1}</div>
                          </td>
                          <td className="px-6 py-3.5 text-left">
                             <div className="flex items-center gap-3">
                                <TeamIcon icon={team.icon} className="w-8 h-8 shadow-sm" />
                                <div className="flex flex-col">
                                   <div className="flex items-center gap-2">
                                      <span className="font-bold text-xs md:text-sm text-slate-800 uppercase italic truncate max-w-[120px]">{String(team.name)}</span>
                                      {isChampion && <Crown className="w-3 h-3 text-yellow-500" />}
                                   </div>
                                   {isChampion && <span className="text-[7px] font-black text-red-600 uppercase tracking-widest italic">Champion of Nexus</span>}
                                </div>
                             </div>
                          </td>
                          <td className="px-6 py-3.5 text-xs font-mono text-slate-400">{Number(team.stats?.played || 0)}</td>
                          <td className="px-6 py-3.5 text-xs font-bold text-green-600">{Number(team.stats?.win || 0)}</td>
                          <td className="px-6 py-3.5 text-xs font-bold text-slate-100">{Number(team.stats?.loss || 0)}</td>
                          <td className="px-6 py-3.5 text-right pr-10">
                            <span className="text-lg md:text-xl font-black text-slate-900 tracking-tighter group-hover:text-red-600 transition-all">
                              {Number((team.stats?.points || 0) + (team.stats?.bonusPoints || 0))}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="space-y-10 animate-in slide-in-from-right-10 duration-500 pb-20">
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
               <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-black uppercase text-slate-400 italic">Tournament Progress</span>
                  <span className="text-[10px] font-bold text-red-600 uppercase tracking-widest animate-pulse">
                     {isTournamentFinished ? "Finished" : isGroupPhaseFinished ? "Fase Final Ready" : "Phase 1: League"}
                  </span>
               </div>
               <div className="relative h-1.5 bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                  <div className="absolute top-0 left-0 h-full bg-red-600 transition-all duration-1000 shadow-[0_0_10px_rgba(220,38,38,0.5)]" style={{ width: `${(schedules.filter(s => s.status === 'completed').length / (schedules.length || 1)) * 100}%` }}></div>
               </div>
            </div>

            {schedules.length === 0 ? (
              <div className="py-24 text-center bg-white rounded-3xl border-2 border-dashed border-slate-100">
                <p className="text-slate-300 font-bold uppercase italic text-xs">No Active Battle Scheme</p>
                <button onClick={generateSchedule} className="mt-3 text-red-600 font-bold text-[10px] uppercase hover:underline">Build Matrix</button>
              </div>
            ) : (
              <div className="space-y-12">
                {/* League Phase */}
                <div>
                  <div className="flex items-center gap-2 mb-6 px-2">
                     <TrendingUp className="w-4 h-4 text-blue-500" />
                     <h3 className="font-black text-[10px] uppercase italic text-slate-900 tracking-widest border-l-4 border-blue-500 pl-3">Group League Phase</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {schedules.filter(s => s.type === 'group').map(sch => (
                      <div key={sch.id} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm relative group hover:border-blue-200 transition-all">
                        <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-3">
                           <span className="text-[9px] font-black uppercase text-blue-500 italic">Group Match</span>
                           {sch.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 text-center">
                            <TeamIcon icon={teams.find(t => t.id === sch.team1Id)?.icon} className="w-10 h-10 mx-auto mb-3 shadow-md" />
                            <p className="text-[11px] font-black text-slate-800 uppercase italic truncate">{sch.team1Name || "---"}</p>
                          </div>
                          <span className="font-black text-slate-100 italic text-xl">VS</span>
                          <div className="flex-1 text-center">
                            <TeamIcon icon={teams.find(t => t.id === sch.team2Id)?.icon} className="w-10 h-10 mx-auto mb-3 shadow-md" />
                            <p className="text-[11px] font-black text-slate-800 uppercase italic truncate">{sch.team2Name || "---"}</p>
                          </div>
                        </div>
                        {sch.status !== 'completed' && (
                          <button onClick={() => { setTeam1Id(sch.team1Id); setTeam2Id(sch.team2Id); setCurrentScheduleId(sch.id); setShowMatchForm(true); }} className="w-full mt-6 py-2.5 bg-slate-50 hover:bg-blue-600 text-slate-400 hover:text-white text-[10px] font-black uppercase rounded-xl transition-all flex items-center justify-center gap-2 italic shadow-inner">Start War</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grand Final Apex */}
                <div className="pt-10">
                  <div className={`bg-white rounded-[3rem] border-2 p-10 md:p-14 text-center relative transition-all duration-700 ${isGroupPhaseFinished ? 'border-red-600 shadow-[0_20px_50px_rgba(220,38,38,0.15)]' : 'border-slate-50 opacity-60'}`}>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent"></div>
                    <span className={`text-[12px] font-black uppercase tracking-[0.5em] italic block mb-10 ${isGroupPhaseFinished ? 'text-red-600 animate-pulse' : 'text-slate-300'}`}>Highest Peak: Grand Final</span>
                    
                    <div className="flex items-center justify-around gap-8 max-w-2xl mx-auto">
                      <div className="flex-1 flex flex-col items-center">
                         <TeamIcon isPlaceholder={!isGroupPhaseFinished} icon={isGroupPhaseFinished ? sortedTeams[0]?.icon : null} className="w-20 h-20 md:w-28 md:h-28 mb-6 shadow-2xl rounded-3xl" />
                         <h4 className="font-black text-sm md:text-2xl uppercase italic tracking-tighter text-slate-900">{isGroupPhaseFinished ? sortedTeams[0]?.name : "Peringkat #1"}</h4>
                      </div>
                      <div className="font-black text-3xl md:text-6xl italic text-slate-100 select-none drop-shadow-sm">VS</div>
                      <div className="flex-1 flex flex-col items-center">
                         <TeamIcon isPlaceholder={!isGroupPhaseFinished} icon={isGroupPhaseFinished ? sortedTeams[1]?.icon : null} className="w-20 h-20 md:w-28 md:h-28 mb-6 shadow-2xl rounded-3xl" />
                         <h4 className="font-black text-sm md:text-2xl uppercase italic tracking-tighter text-slate-900">{isGroupPhaseFinished ? sortedTeams[1]?.name : "Peringkat #2"}</h4>
                      </div>
                    </div>

                    {!isGroupPhaseFinished ? (
                      <div className="mt-14 inline-flex items-center gap-3 px-6 py-2 bg-slate-50 rounded-full text-slate-300 font-bold text-[10px] uppercase italic border border-slate-100">
                         <Loader2 className="w-4 h-4 animate-spin" /> Waiting for Final Squads
                      </div>
                    ) : isTournamentFinished ? (
                        <div className="mt-14 inline-flex items-center gap-3 px-8 py-3 bg-green-50 rounded-full text-green-600 font-black text-xs uppercase italic border border-green-200">
                            <CheckCircle2 className="w-5 h-5" /> Tournament Over
                        </div>
                    ) : (
                      <button onClick={() => {
                        const final = schedules.find(s => s.type === 'final');
                        setTeam1Id(sortedTeams[0].id); setTeam2Id(sortedTeams[1].id); setCurrentScheduleId(final.id); setShowMatchForm(true);
                      }} className="mt-14 bg-red-600 hover:bg-red-500 text-white px-14 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl active:scale-95 transition-all flex items-center gap-4 italic group">
                        <Play className="w-5 h-5 fill-white" /> Start Final Battle
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'teams' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-in slide-in-from-bottom-5 duration-500">
            {teams.map(team => (
              <div key={team.id} className="bg-white rounded-2xl border border-slate-100 p-6 group shadow-sm hover:shadow-md transition-all text-left">
                <div className="flex justify-between items-start mb-8">
                  <div className="flex items-center gap-4">
                    <TeamIcon icon={team.icon} className="w-14 h-14 rounded-xl shadow-sm" />
                    <div>
                      <h3 className="text-lg font-black text-slate-900 uppercase italic leading-none group-hover:text-red-600 transition-colors">{String(team.name)}</h3>
                      <p className="text-[9px] text-slate-400 font-bold uppercase mt-2 tracking-widest leading-none">Registered Squad</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => openTeamForm(team)} className="p-2.5 bg-slate-50 text-slate-300 hover:text-blue-600 rounded-lg transition-all border border-slate-100 shadow-inner"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => { if(window.confirm("Delete Squad?")) deleteDoc(doc(db, 'artifacts', TOURNAMENT_ID, 'public', 'data', 'teams', team.id)) }} className="p-2.5 bg-slate-50 text-slate-300 hover:text-red-600 rounded-lg transition-all border border-slate-100 shadow-inner"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {team.players.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50/50 border border-slate-100/50">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-black text-red-600/30 italic w-4">#{i+1}</span>
                        <div>
                          <p className="font-bold text-xs text-slate-700 uppercase italic">{String(p.name || "---")}</p>
                          <p className="text-[8px] text-slate-400 font-mono">ID:{String(p.userId || "0")}</p>
                        </div>
                      </div>
                      <span className="text-[8px] font-black uppercase text-slate-400 px-2 py-0.5 rounded bg-white border border-slate-100 leading-none">{String(p.role).slice(0, 3)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-4 animate-in slide-in-from-right-10 duration-700 pb-20">
            {matches.map(match => (
              <div key={match.id} className="bg-white rounded-2xl border border-slate-100 p-6 flex flex-col md:flex-row items-center justify-between gap-6 group shadow-sm hover:border-red-100 transition-all relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-600 opacity-10 group-hover:opacity-100 transition-all"></div>
                <div className="flex-1 flex flex-row items-center gap-5 w-full">
                  <TeamIcon icon={match.team1.icon} className="w-10 h-10 shadow-sm" />
                  <div className="text-left">
                    <h4 className={`text-sm md:text-base font-black uppercase italic ${match.team1.isWinner ? 'text-red-600' : 'text-slate-400'}`}>{String(match.team1.name)}</h4>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {match.team1.isWinner && <span className="text-red-600 text-[7px] font-black px-1.5 py-0.5 rounded bg-red-50 uppercase border border-red-100">WINNER</span>}
                      {match.team1.bonus.maniac && <span className="bg-purple-50 text-purple-600 text-[7px] font-black px-1.5 py-0.5 rounded border border-purple-100 uppercase">+1 MANIAC</span>}
                      {match.team1.bonus.savage && <span className="bg-orange-50 text-orange-600 text-[7px] font-black px-1.5 py-0.5 rounded border border-orange-100 uppercase">+2 SAVAGE</span>}
                    </div>
                  </div>
                </div>
                <div className="text-slate-100 font-black italic text-sm select-none">VS</div>
                <div className="flex-1 flex flex-row-reverse items-center gap-5 w-full text-right">
                  <TeamIcon icon={match.team2.icon} className="w-10 h-10 shadow-sm" />
                  <div>
                    <h4 className={`text-sm md:text-base font-black uppercase italic ${match.team2.isWinner ? 'text-red-600' : 'text-slate-400'}`}>{String(match.team2.name)}</h4>
                    <div className="flex flex-wrap justify-end gap-1.5 mt-2">
                      {match.team2.isWinner && <span className="text-red-600 text-[7px] font-black px-1.5 py-0.5 rounded bg-red-50 uppercase border border-red-100">WINNER</span>}
                      {match.team2.bonus.maniac && <span className="bg-purple-50 text-purple-600 text-[7px] font-black px-1.5 py-0.5 rounded border border-purple-100 uppercase">+1 MANIAC</span>}
                      {match.team2.bonus.savage && <span className="bg-orange-50 text-orange-600 text-[7px] font-black px-1.5 py-0.5 rounded border border-orange-100 uppercase">+2 SAVAGE</span>}
                    </div>
                  </div>
                </div>
                <div className="text-slate-300 text-[7px] font-mono font-bold uppercase bg-slate-50 px-2 py-0.5 rounded border border-slate-100 leading-none">{String(match.date)}</div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* MODALS */}
      {showTeamForm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-xl bg-slate-900/5">
          <div className="bg-white border border-slate-200 rounded-[2.5rem] w-full max-w-lg p-10 animate-in zoom-in-95 duration-200 shadow-2xl">
            <h2 className="text-xl font-black uppercase italic text-slate-900 mb-8 tracking-tighter">Nexus Enrollment</h2>
            <form onSubmit={saveTeam} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase italic">Squad Callsign</label>
                  <input type="text" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} className="w-full bg-slate-50 border border-slate-100 rounded-xl p-3.5 text-sm font-black uppercase text-red-600 outline-none focus:bg-white" placeholder="NAME" required />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-400 uppercase italic">Icon (URL/Emoji)</label>
                  <input type="text" value={newTeamIcon} onChange={(e) => setNewTeamIcon(e.target.value)} className="w-full bg-slate-50 border border-slate-100 rounded-xl p-3.5 text-[10px] font-bold outline-none text-slate-700 focus:bg-white" placeholder="🛡️ / URL" />
                </div>
              </div>
              <div className="space-y-2 pt-4 border-t border-slate-50">
                {newPlayers.map((player, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input type="text" placeholder={`Nick (${player.role})`} value={player.name} onChange={(e) => { const updated = [...newPlayers]; updated[idx].name = e.target.value; setNewPlayers(updated); }} className="flex-[2] bg-slate-50 border border-slate-100 rounded-lg p-2.5 font-bold text-xs outline-none focus:bg-white transition-all uppercase" required />
                    <input type="text" placeholder="UID" value={player.userId} onChange={(e) => { const updated = [...newPlayers]; updated[idx].userId = e.target.value; setNewPlayers(updated); }} className="flex-1 bg-slate-50 border border-slate-100 rounded-lg p-2.5 font-mono text-[9px] outline-none focus:bg-white uppercase" required />
                  </div>
                ))}
              </div>
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowTeamForm(false)} className="flex-1 py-4 rounded-2xl font-bold uppercase text-[10px] tracking-widest text-slate-400">Cancel</button>
                <button type="submit" className="flex-[2] py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest bg-red-600 text-white shadow-xl active:scale-95 transition-all italic">Save Squad</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMatchForm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-xl bg-slate-900/5 overflow-y-auto">
          <div className="bg-white border border-white rounded-[2.5rem] w-full max-w-4xl shadow-2xl p-12 animate-in zoom-in-95 duration-200">
            <h2 className="text-xl md:text-2xl font-black uppercase italic text-slate-900 mb-10 text-center tracking-tighter border-b border-slate-50 pb-5">Battle Matrix Resolution</h2>
            <form onSubmit={addMatch} className="space-y-12">
              <div className="grid grid-cols-2 gap-8 md:gap-12">
                <div className="space-y-3 text-center">
                  <p className="text-[9px] font-black text-blue-500 uppercase italic tracking-widest">Alliance (Blue)</p>
                  <div className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-6 font-black uppercase text-lg text-slate-800 shadow-inner">{teams.find(t => t.id === team1Id)?.name || "---"}</div>
                </div>
                <div className="space-y-3 text-center">
                  <p className="text-[9px] font-black text-red-500 uppercase italic tracking-widest">Opposition (Red)</p>
                  <div className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-6 font-black uppercase text-lg text-slate-800 shadow-inner">{teams.find(t => t.id === team2Id)?.name || "---"}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8 md:gap-12 border-t border-slate-50 pt-10 text-center">
                {[1, 2].map(num => {
                  const tid = num === 1 ? team1Id : team2Id;
                  const isWinner = winnerId === tid;
                  const currentTeam = teams.find(t => t.id === tid);
                  return (
                    <div key={num} className={`p-8 rounded-3xl border-2 transition-all duration-300 ${isWinner ? 'border-red-100 bg-red-50/20 shadow-lg' : 'border-slate-50 bg-slate-50/50'}`}>
                      <div className="flex flex-col items-center mb-8">
                        <TeamIcon icon={currentTeam?.icon} className="w-12 h-12 mb-4 shadow-sm" />
                        <p className="text-xl font-black text-center uppercase text-slate-900 leading-none italic">{String(currentTeam?.name)}</p>
                      </div>
                      <button type="button" onClick={() => setWinnerId(tid)} className={`w-full py-4 rounded-xl font-black uppercase text-[10px] italic border-2 transition-all ${isWinner ? 'bg-red-600 border-red-500 text-white shadow-xl shadow-red-100' : 'bg-white border-slate-200 text-slate-300 hover:text-slate-600'}`}>
                         {isWinner ? '🏆 Victor Verified' : 'Mark Winner'}
                      </button>
                      <div className="mt-8 flex flex-col gap-2.5 pt-8 border-t border-slate-100 text-left">
                         <label onClick={() => toggleBonus(num, 'maniac')} className={`flex items-center justify-between p-4 rounded-2xl cursor-pointer border-2 transition-all ${((num === 1 && bonusT1.maniac) || (num === 2 && bonusT2.maniac)) ? 'bg-white border-red-200 text-red-600 shadow-sm' : 'border-slate-100 text-slate-300 hover:border-slate-200'}`}>
                           <span className="font-black text-[9px] tracking-widest italic uppercase">Maniac</span>
                           <div className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${((num === 1 && bonusT1.maniac) || (num === 2 && bonusT2.maniac)) ? 'bg-red-600 border-red-600' : 'border-slate-200'}`}></div>
                         </label>
                         <label onClick={() => toggleBonus(num, 'savage')} className={`flex items-center justify-between p-4 rounded-2xl cursor-pointer border-2 transition-all ${((num === 1 && bonusT1.savage) || (num === 2 && bonusT2.savage)) ? 'bg-white border-red-200 text-red-600 shadow-sm' : 'border-slate-100 text-slate-300 hover:border-slate-200'}`}>
                           <span className="font-black text-[9px] tracking-widest italic uppercase">Savage</span>
                           <div className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${((num === 1 && bonusT1.savage) || (num === 2 && bonusT2.savage)) ? 'bg-red-600 border-red-600' : 'border-slate-200'}`}></div>
                         </label>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-4 md:gap-6 border-t border-slate-100 pt-10 pb-10">
                <button type="button" onClick={() => {setShowMatchForm(false); setCurrentScheduleId(null);}} className="flex-1 py-5 rounded-[2rem] font-bold uppercase text-[10px] tracking-widest text-slate-400 hover:bg-slate-50">Discard</button>
                <button type="submit" disabled={!winnerId} className={`flex-[2] py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-widest shadow-2xl active:scale-95 italic ${winnerId ? 'bg-red-600 text-white shadow-red-100' : 'bg-slate-100 text-slate-300 cursor-not-allowed'}`}>Broadcast Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Style Global */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
        body { font-family: 'Inter', sans-serif; background: #FDFDFD; }
        .custom-scrollbar::-webkit-scrollbar { height: 5px; width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #F1F5F9; border-radius: 10px; }
        ::selection { background: #FEE2E2; color: #DC2626; }
      `}</style>
    </div>
  );
};

export default App;